import { api } from '@/api/client';
import type { TenantMembership } from '@/api/auth';

export interface TenantMember {
  user_id: string;
  email: string;
  public_key: string; // base64
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joined_at: string;
}

export interface CreateTenantRequest {
  name: string;
  slug?: string;
}

interface CreateTenantResponse {
  tenant_id: string;
  name: string;
  slug: string;
  role: TenantMembership['role'];
}

export interface MemberRemoved {
  shares_revoked: number;
  ownership_transferred: number;
  /** Resurse la care membrul eliminat a avut acces — DEK-ul lor ar trebui rotit. */
  resources_needing_rotation: number;
}

export interface DeletionPreview {
  name: string;
  /** Ceilalți membri, fără userul curent. */
  other_member_count: number;
  /** Primii 5, pentru dialogul de confirmare. */
  other_members: { email: string; role: TenantMember['role'] }[];
  credential_count: number;
  note_count: number;
  shared_item_count: number;
}

export const tenantsApi = {
  deletionPreview: (tenantId: string) =>
    api.get<DeletionPreview>(`/tenants/${tenantId}/deletion-preview`),

  /** Ștergere soft; doar owner, cu parola master re-verificată. */
  deleteTenant: (tenantId: string, body: { auth_hash: string; confirm_name: string }) =>
    api.post<{ tenant_id: string; members_affected: number }>(
      `/tenants/${tenantId}/delete`,
      body,
    ),

  listMembers: (tenantId: string) =>
    api.get<TenantMember[]>(`/tenants/${tenantId}/members`),

  changeMemberRole: (tenantId: string, userId: string, role: TenantMember['role']) =>
    api.patch<{ user_id: string; role: string }>(`/tenants/${tenantId}/members/${userId}`, {
      role,
    }),

  /** Cu propriul `userId`: părăsește tenantul. */
  removeMember: (tenantId: string, userId: string) =>
    api.delete<MemberRemoved>(`/tenants/${tenantId}/members/${userId}`),

  create: async (req: CreateTenantRequest): Promise<TenantMembership> => {
    const resp = await api.post<CreateTenantResponse>('/tenants', req);
    return {
      tenant_id: resp.tenant_id,
      name: resp.name,
      slug: resp.slug,
      role: resp.role,
    };
  },
};
